import { ArrowOutward } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Avatar, Button } from "@mui/material";
import cx from "classnames";
import { useFormik, Field, FormikProvider } from "formik";
import moment from "moment-timezone";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ReactComponent as BellIcon } from "assets/bell.svg";
import { ReactComponent as CheckIcon } from "assets/check-icon.svg";
import { ReactComponent as CrownIcon } from "assets/crown.svg";
import { ReactComponent as GearIcon } from "assets/gear.svg";
import { ReactComponent as HotspringIcon } from "assets/hotspring.svg";
import slapshotIcon from "assets/logo_icon.png";
import { ReactComponent as LogoutIcon } from "assets/logout-icon.svg";
import { ReactComponent as PrivacyIcon } from "assets/privacy-security.svg";
import { ReactComponent as ProfileIcon } from "assets/profile-icon.svg";
import AutopilotUsageTab from "components/Payment/UpgradeFlowComponents/AutopilotUsageTab";
import SlapshotSwitch from "components/SlapshotSwitch";
import { WINDOW_EVENTS } from "constants/constants";
import { passwordYupRule } from "constants/passwordValidation";
import { Subscription } from "models/User";
import DeveloperSection from "pages/NewProfile/DeveloperSection";
import UpgradePlanFlow, { UpgradePlanFlowRef } from "pages/UpgradePlanFlow";
import {
  getSocket,
  getRequestData,
  ACTION_SUBSCRIBE_SUBSCRIPTION_UPDATE,
} from "services/WebSocketService";

import styles from "./ProfilePage.module.scss";
import TwoFactorSection from "./TwoFactorSection";
import { InputField } from "../../components/InputField";
import {
  SLAPSHOT_API_URL,
  SLAPSHOT_LOGIN_URL,
  SLAPSHOT_MY_PLAN_URL,
  SLAPSHOT_MY_PLAN_UPGRADE_URL,
  SLAPSHOT_VERIFY_EMAIL_URL,
} from "../../constants/urls";
import {
  generateAPIKey,
  changePassword,
  updateUserInfo,
  getUserInfo,
  updateProfilePicture,
  updateUserNotificationCategories,
  getSubscription,
} from "../../services/UserService";
import { formatSize, getInitials, getUser } from "../../utils/helper";

export interface UserInfo {
  name: string;
  last_name: string;
  email: string;
  phone: string;
  profile_picture_url: string;
  has_autopilot_integration: boolean;
}

export const PROFILE_INDEX = 1;
export const PRIVACY_SECURITY_INDEX = 2;
export const NOTIFICATIONS_INDEX = 5;
export const INTEGRATION_HOTSPRING_INDEX = 6;
export const UPGRADE_PLAN_INDEX = 7;
export const INTEGRATION_AUTOPILOT_INDEX = 8;
export const DEVELOPER_INDEX = 9;

const ProfilePage = () => {
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [apiKey, setApiKey] = useState("");

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [notificationCategories, setNotificationCategories] = useState<
    string[]
  >([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const upgradePlanFlowRef = useRef<UpgradePlanFlowRef>(null);

  const [tabIndex, setTabIndex] = useState<number | null>(null);

  const showHotspringIntegration =
    user.email.substring(user.email.lastIndexOf("@")) === "@thehotspring.com";
  const showAutopilotIntegration = !!userInfo?.has_autopilot_integration;

  const handleChange = (tab: number) => {
    setTabIndex(tab);
  };

  const timezones = moment.tz.names();

  const formatTimezone = (timezone) => {
    const dt = moment.tz(timezone);
    const offset = dt.utcOffset();
    const sign = offset >= 0 ? "+" : "-";
    const absOffset = Math.abs(offset);
    const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
    const minutes = String(absOffset % 60).padStart(2, "0");
    const formattedOffset = `UTC ${sign}${hours}:${minutes}`;

    return `${formattedOffset} - ${timezone}`;
  };

  const changePasswordFormik = useFormik({
    initialValues: {
      password: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string().required("Current password is required."),
      newPassword: passwordYupRule.label("New password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Please retype your password"),
    }),
    onSubmit: async (values, { setStatus, setFieldError, resetForm }) => {
      try {
        const res = await changePassword(values.password, values.newPassword);
        resetForm();
        setStatus(res ?? "Successfully changed the password");
      } catch (err) {
        // Route the 400 detail to the field it actually concerns:
        // "Incorrect current password" → old-password input, anything else
        // (validate_password output) → new-password input.
        const detail = (err as { detail?: string } | undefined)?.detail;
        if (!detail) {
          setFieldError(
            "newPassword",
            "Could not change password. Please try again.",
          );
          return;
        }
        const isCurrentPasswordError = /current password/i.test(detail);
        setFieldError(
          isCurrentPasswordError ? "password" : "newPassword",
          detail,
        );
      }
    },
  });

  const updateProfileFormik = useFormik({
    initialValues: {
      name: userInfo?.name || "",
      lastName: userInfo?.last_name || "",
      contactNumber: userInfo?.phone || "",
      emailId: userInfo?.email || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("First name is required"),
      lastName: Yup.string().nullable(),
      contactNumber: Yup.string().nullable(),
      emailId: Yup.string().email().required("Email Id is required"),
    }),
    onSubmit: async (values, { setValues, setFieldError }) => {
      const updatedValues = {
        ...values,
        emailId: values.emailId.toLowerCase(),
      };
      try {
        const res = await updateUserInfo(updatedValues);
        if (res.verification_required) {
          navigate(SLAPSHOT_VERIFY_EMAIL_URL, {
            state: { email: res.data.email, id: res.data.id },
          });
          return;
        }
        setUserInfo(res.data);
        setValues({
          name: res.data.name || "",
          lastName: res.data.last_name || "",
          contactNumber: res.data.phone || "",
          emailId: res.data.email || "",
        });
      } catch (err) {
        // The only 400 here is a rejected email (blocked domain, bad address).
        const detail = (err as { detail?: string } | undefined)?.detail;
        setFieldError(
          "emailId",
          detail || "Could not update your profile. Please try again.",
        );
      }
    },
  });

  const updateFormValues = useCallback(
    (values: UserInfo) => {
      updateProfileFormik.setValues({
        name: values.name || "",
        lastName: values.last_name || "",
        contactNumber: values.phone || "",
        emailId: values.email || "",
      });
    },
    [updateProfileFormik],
  );

  useEffect(() => {
    if (!userInfo) {
      getUserInfo().then((res) => {
        setUserInfo(res);
        updateFormValues(res);
        setNotificationCategories(res.notification_categories);
      });
    }
  }, [updateFormValues, userInfo]);

  const handleUpdate = (c: string) => {
    const updatedCategories = !notificationCategories.includes(c)
      ? [...notificationCategories, c]
      : notificationCategories.filter((category) => category !== c);
    updateUserNotificationCategories(updatedCategories).then((res) => {
      setNotificationCategories(res);
    });
  };

  useEffect(() => {
    getSubscription().then((data) => {
      setSubscription(data);
    });
  }, []);

  useEffect(() => {
    if (!subscription) return () => {};
    window.dispatchEvent(
      new CustomEvent(WINDOW_EVENTS.SHOW_PLAN_TAG, {
        detail: { plan: subscription.plan.name },
      }),
    );

    return () => {
      window.dispatchEvent(new CustomEvent(WINDOW_EVENTS.HIDE_PLAN_TAG));
    };
  });

  useEffect(() => {
    if (location.pathname.startsWith(SLAPSHOT_MY_PLAN_URL)) {
      setTabIndex(UPGRADE_PLAN_INDEX);
    } else if (location.pathname.startsWith(SLAPSHOT_API_URL)) {
      setTabIndex(DEVELOPER_INDEX);
    } else {
      setTabIndex(PROFILE_INDEX);
    }
  }, [location.pathname]);

  const subscriptionSocket = useMemo(() => {
    if (subscription?.id) return getSocket("subscription");
    return null;
  }, [subscription?.id]);

  // Subscribing to subscription updates
  useEffect(() => {
    if (subscriptionSocket && subscription)
      subscriptionSocket.onopen = (event) => {
        subscriptionSocket.send(
          getRequestData(ACTION_SUBSCRIBE_SUBSCRIPTION_UPDATE, {
            subscription_id: subscription.id,
          }),
        );
      };
  }, [subscription, subscriptionSocket]);

  useEffect(() => {
    if (subscriptionSocket)
      subscriptionSocket.onmessage = (event) => {
        const { data } = JSON.parse(event.data);
        setSubscription(data);
      };

    return () => {
      subscriptionSocket?.close();
    };
  }, [subscriptionSocket]);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.sidebar}>
        <div className={styles.sidebarContent}>
          <div className={styles.profileInfo}>
            {userInfo && userInfo.profile_picture_url ? (
              <img
                src={userInfo.profile_picture_url}
                className={styles.sidebarProfile}
                alt={userInfo.profile_picture_url}
              />
            ) : (
              <Avatar
                className={styles.avatarButton}
                {...{ children: getInitials() }}
                sx={{ bgcolor: "purple", width: 46, height: 46 }}
              />
            )}
            <div className={styles.userDetails}>
              <h3>{user.name}</h3>
              {/* TODO: fetch role from db */}
              <p>Super admin</p>
            </div>
          </div>
          <div className={styles.accountSettings}>
            <h5>ACCOUNT SETTINGS</h5>
            <div className={styles.settingsMenu}>
              <Button
                className={cx(
                  styles.menuButton,
                  tabIndex === PROFILE_INDEX && styles.selectedMenuButton,
                )}
                onClick={() => {
                  handleChange(PROFILE_INDEX);
                }}
              >
                <ProfileIcon className={styles.menuButtonIcon} />
                Profile
              </Button>
              <Button
                className={cx(
                  styles.menuButton,
                  tabIndex === PRIVACY_SECURITY_INDEX &&
                    styles.selectedMenuButton,
                )}
                onClick={() => {
                  handleChange(PRIVACY_SECURITY_INDEX);
                }}
              >
                <PrivacyIcon className={styles.menuButtonIcon} />
                Privacy & Security
              </Button>
              <Button
                className={cx(
                  styles.menuButton,
                  tabIndex === NOTIFICATIONS_INDEX && styles.selectedMenuButton,
                )}
                onClick={() => {
                  handleChange(NOTIFICATIONS_INDEX);
                }}
              >
                <BellIcon className={styles.menuButtonIcon} />
                Notifications
              </Button>

              <Button
                className={cx(
                  styles.menuButton,
                  tabIndex === UPGRADE_PLAN_INDEX && styles.selectedMenuButton,
                )}
                onClick={() => {
                  navigate(
                    (subscription?.plan.price.unit_amount_decimal || 0) > 0
                      ? SLAPSHOT_MY_PLAN_URL
                      : SLAPSHOT_MY_PLAN_UPGRADE_URL,
                  );
                  handleChange(UPGRADE_PLAN_INDEX);
                }}
              >
                <CrownIcon className={styles.menuButtonIcon} />
                {`${(subscription?.plan.price.unit_amount_decimal || 0) > 0 ? "My" : "Upgrade"} Plan`}
              </Button>
            </div>
          </div>
          <div className={styles.accountSettings}>
            <h5>INTEGRATIONS</h5>
            <div className={styles.settingsMenu}>
              <Button
                className={cx(
                  styles.menuButton,
                  tabIndex === DEVELOPER_INDEX && styles.selectedMenuButton,
                )}
                onClick={() => {
                  navigate(SLAPSHOT_API_URL);
                  handleChange(DEVELOPER_INDEX);
                }}
              >
                <GearIcon className={styles.menuButtonIcon} />
                ComfyUI
              </Button>

              {showHotspringIntegration && (
                <Button
                  className={cx(
                    styles.menuButton,
                    tabIndex === INTEGRATION_HOTSPRING_INDEX &&
                      styles.selectedMenuButton,
                  )}
                  onClick={() => {
                    handleChange(INTEGRATION_HOTSPRING_INDEX);
                  }}
                >
                  <HotspringIcon className={styles.menuButtonIcon} />
                  Hotspring
                </Button>
              )}

              {showAutopilotIntegration && (
                <Button
                  className={cx(
                    styles.menuButton,
                    tabIndex === INTEGRATION_AUTOPILOT_INDEX &&
                      styles.selectedMenuButton,
                  )}
                  onClick={() => {
                    handleChange(INTEGRATION_AUTOPILOT_INDEX);
                  }}
                >
                  <img
                    src={slapshotIcon}
                    alt="Autopilot"
                    className={styles.autopilotIcon}
                  />
                  Autopilot
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.sidebarFooter}>
          {subscription && (
            <div className={styles.planSummary}>
              <div className={styles.psLeft}>
                <div
                  className={styles.psHeader}
                >{`${subscription.plan.name.toUpperCase()} PLAN`}</div>
                <div className={styles.psBody}>
                  <div className={styles.psSection}>Assets storage:</div>
                  <div className={styles.psValue}>
                    <span className={styles.psPrimary}>
                      {formatSize(subscription.storage_used)}
                    </span>
                    <span
                      className={styles.psSecondary}
                    >{` / ${formatSize(subscription.plan.storage, "GB")}`}</span>
                  </div>
                </div>
              </div>
              <div className={styles.psRight}>
                <div
                  className={styles.psLightHeader}
                  onClick={() => {
                    navigate(`${SLAPSHOT_MY_PLAN_URL}?tab=usage`);
                    handleChange(UPGRADE_PLAN_INDEX);
                  }}
                >
                  Usage <ArrowOutward />
                </div>
                <div className={styles.psBody}>
                  <div className={styles.psSection}>Export frames used:</div>
                  <div className={styles.psValue}>
                    <span className={styles.psPrimary}>
                      {subscription.frames_used}
                    </span>
                    <span
                      className={styles.psSecondary}
                    >{` / ${subscription.frames_limit}`}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <Button
            className={styles.logoutButton}
            onClick={async () => {
              localStorage.clear();
              navigate(SLAPSHOT_LOGIN_URL);
            }}
          >
            <LogoutIcon className={styles.menuButtonIcon} />
            Log out
          </Button>
        </div>
      </div>
      <div className={styles.container}>
        {tabIndex === PROFILE_INDEX && (
          <div className={styles.tabPanal}>
            <div className={styles.contentHeader}>
              <h3>Profile Information</h3>
            </div>
            <div className={styles.innerContainer}>
              <FormikProvider value={updateProfileFormik}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateProfileFormik.handleSubmit();
                  }}
                  className={styles.profileFormContainer}
                >
                  <div className={styles.avatarRow}>
                    <h5>Avatar</h5>
                    <div className={styles.avatarContainer}>
                      {userInfo && userInfo.profile_picture_url ? (
                        <img
                          src={userInfo.profile_picture_url}
                          className={styles.profileImage}
                          alt={userInfo.profile_picture_url}
                        />
                      ) : (
                        <Avatar
                          className={styles.avatarButton}
                          {...{ children: getInitials() }}
                          sx={{ bgcolor: "purple", width: 70, height: 70 }}
                        />
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append("profile_picture", file);
                            updateProfilePicture(formData).then((res) => {
                              setUserInfo(res);
                            });
                          }
                        }}
                      />
                      <Button
                        className="noBackgroundButton"
                        onClick={() => {
                          fileInputRef.current?.click();
                        }}
                      >
                        Upload Photo
                      </Button>
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <Field
                      name="name"
                      component={InputField}
                      inputClassName={styles.profileInput}
                      label="First Name"
                      placeholder="Ex: Jon"
                    />
                    <Field
                      name="lastName"
                      component={InputField}
                      inputClassName={styles.profileInput}
                      label="Last Name"
                    />
                  </div>
                  <div className={styles.formRow}>
                    {/* Set by phone verification and unique per account, so
                        it is shown but not editable here. */}
                    <Field
                      name="contactNumber"
                      component={InputField}
                      inputClassName={styles.profileInput}
                      label="Contact Number"
                      placeholder="Not verified"
                      disabled
                    />
                    <Field
                      name="emailId"
                      component={InputField}
                      inputClassName={styles.profileInput}
                      label="Email Id"
                      placeholder="John@example.com"
                    />
                  </div>
                  <div className={styles.formActions}>
                    <Button
                      className="noBackgroundButton"
                      onClick={() => {
                        if (userInfo) updateFormValues(userInfo);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="slapShotButton"
                      onClick={() => {
                        updateProfileFormik.handleSubmit();
                      }}
                    >
                      Save Changes
                    </Button>
                  </div>
                </form>
              </FormikProvider>
            </div>
          </div>
        )}
        {tabIndex === UPGRADE_PLAN_INDEX && (
          <div className={styles.tabPanal}>
            <UpgradePlanFlow
              ref={upgradePlanFlowRef}
              subscription={subscription}
            />
          </div>
        )}
        {tabIndex === PRIVACY_SECURITY_INDEX && (
          <div className={styles.tabPanal}>
            <div className={styles.contentHeader}>
              <h3>Privacy & Security</h3>
            </div>
            <div className={styles.innerContainer}>
              <FormikProvider value={changePasswordFormik}>
                <form
                  className={styles.changePasswordForm}
                  onSubmit={(e) => {
                    e.preventDefault();
                    changePasswordFormik.handleSubmit();
                  }}
                >
                  {changePasswordFormik.status && (
                    <p className={styles.formStatus}>
                      <CheckIcon style={{ marginRight: "8px" }} />
                      Successfully changed the password
                    </p>
                  )}
                  <Field
                    name="password"
                    inputClassName={styles.profileInput}
                    component={InputField}
                    label="Old password"
                    type="password"
                  />
                  <Field
                    name="newPassword"
                    inputClassName={styles.profileInput}
                    component={InputField}
                    label="New password"
                    type="password"
                  />
                  <Field
                    name="confirmPassword"
                    inputClassName={styles.profileInput}
                    component={InputField}
                    label="Confirm Password"
                    type="password"
                  />
                  <div className={styles.formActions}>
                    <Button
                      className="noBackgroundButton"
                      onClick={() => {
                        changePasswordFormik.resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="slapShotButton"
                      onClick={() => {
                        changePasswordFormik.handleSubmit();
                      }}
                    >
                      Save Changes
                    </Button>
                  </div>
                </form>
              </FormikProvider>
            </div>
            <TwoFactorSection />
          </div>
        )}
        {tabIndex === NOTIFICATIONS_INDEX && (
          <div className={styles.tabPanal}>
            <div className={styles.contentHeader}>
              <h3>Notifications</h3>
            </div>
            <div className={styles.notificationSettings}>
              <div className={styles.notificationSetting}>
                <p>Notify by email when an inference has been completed</p>
                <SlapshotSwitch
                  id="inference-switch"
                  checked={notificationCategories.includes("INFERENCES")}
                  onChange={() => {
                    handleUpdate("INFERENCES");
                  }}
                  labelClassName={styles.customSwitchLabel}
                />
              </div>
              <div className={styles.notificationSetting}>
                <p>Notify by email when an export is ready for download</p>
                <SlapshotSwitch
                  id="exports-switch"
                  checked={notificationCategories.includes("EXPORTS")}
                  onChange={() => {
                    handleUpdate("EXPORTS");
                  }}
                  labelClassName={styles.customSwitchLabel}
                />
              </div>
            </div>
          </div>
        )}

        {tabIndex === INTEGRATION_HOTSPRING_INDEX && (
          <div className={styles.tabPanal}>
            <div className={styles.contentHeader}>
              <h3>Integrations</h3>
            </div>
            <div className={styles.generateAPIKeyContainer}>
              <div className={styles.apiFieldContainer}>
                <h5>Generate API Key</h5>
                <div className={styles.apiKeyField}>{apiKey}</div>
                <button
                  type="button"
                  className={styles.copyButton}
                  onClick={async () => {
                    await navigator.clipboard.writeText(apiKey);
                  }}
                >
                  <ContentCopyIcon />
                </button>
              </div>
              <div className={styles.actionsContainer}>
                <Button
                  className="slapShotButton"
                  onClick={async () => {
                    const res = await generateAPIKey();
                    setApiKey(res.key);
                  }}
                >
                  Generate API Key
                </Button>
              </div>
            </div>
          </div>
        )}

        {tabIndex === INTEGRATION_AUTOPILOT_INDEX && (
          <div className={styles.tabPanal}>
            <div className={styles.contentHeader}>
              <h3>Autopilot</h3>
            </div>
            <AutopilotUsageTab />
          </div>
        )}

        {tabIndex === DEVELOPER_INDEX && (
          <DeveloperSection subscription={subscription} />
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
